import { Paper, Stack, useMediaQuery, useTheme } from "@mui/material";
import {
  BannerCloseButton,
  BannerCta,
  BannerIconTile,
  BannerText,
} from "./BannerParts";
import { BannerCountdown, StackedOffer } from "./PromoParts";
import {
  BANNER_VARIANTS,
  STICKY_BAR_SHADOW,
  bannerSurfaceSx,
  type BannerIconVisual,
  type BannerVariant,
} from "./bannerVariants";

type StickyBottomBarBannerProps = {
  title: string;
  text?: string;
  /** Seconds left on the countdown, shown before the CTA. */
  countdown?: number;
  /** No CTA once the offer is disabled. */
  ctaLabel?: string;
  onCtaClick: () => void;
  onClose?: () => void;
  variant: BannerVariant;
  /** Mobile never shows it. */
  visual?: BannerIconVisual;
};

/**
 * The bar pinned to the bottom of the viewport. Only the bar: where it is
 * fixed in the layout belongs to the placement that renders it.
 */
export const StickyBottomBarBanner = (props: StickyBottomBarBannerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Paper
      elevation={8}
      data-testid="sticky-bottom-bar-banner"
      sx={[
        { boxShadow: STICKY_BAR_SHADOW },
        bannerSurfaceSx(props.variant, { paper: true }),
      ]}
    >
      {isMobile ? (
        <StickyBarMobile {...props} />
      ) : (
        <StickyBarDesktop {...props} />
      )}
    </Paper>
  );
};

const StickyBarDesktop = ({
  title,
  text,
  countdown,
  ctaLabel,
  onCtaClick,
  onClose,
  variant,
  visual,
}: StickyBottomBarBannerProps) => (
  <Stack direction="row" alignItems="center" spacing={3} p={2.5}>
    {visual && (
      <BannerIconTile
        icon={visual.icon}
        tone={BANNER_VARIANTS[variant].iconTone}
        size="small"
      />
    )}
    <BannerText title={title} text={text} variant={variant} size="small" />
    {countdown !== undefined && (
      <BannerCountdown
        seconds={countdown}
        tone={BANNER_VARIANTS[variant].iconTone}
        look="boxes"
      />
    )}
    {ctaLabel && (
      <BannerCta label={ctaLabel} onClick={onCtaClick} variant={variant} />
    )}
    {onClose && <BannerCloseButton onClose={onClose} variant={variant} />}
  </Stack>
);

const StickyBarMobile = ({
  title,
  text,
  countdown,
  ctaLabel,
  onCtaClick,
  onClose,
  variant,
}: StickyBottomBarBannerProps) => (
  <Stack spacing={2.5} pl={3} pr={2.5} py={3}>
    <Stack direction="row" alignItems="flex-start" spacing={2}>
      <BannerText title={title} text={text} variant={variant} size="small" />
      {onClose && <BannerCloseButton onClose={onClose} variant={variant} />}
    </Stack>
    <StackedOffer
      countdown={countdown}
      ctaLabel={ctaLabel}
      onCtaClick={onCtaClick}
      variant={variant}
    />
  </Stack>
);
